import { useCallback, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import dayjs from 'dayjs';
import lodashFilter from 'lodash/filter';
import lodashIsEmpty from 'lodash/isEmpty';

import {
  ItemInfo,
  ReservationType,
} from '../Components/Sheets/BookReservation/types';
import modals from '../Components/Sheets/modals';
import { ModalManager } from '../Components/Web/Modal/hooks/useModalManager';
import constants from '../Config/constants';
import DateHelper from '../Helper/Date';
import routeList from '../Routes/list';
import { EventDetails, EventTimeSlot } from '../RTK/api/events/types';
import { StoreDetails } from '../RTK/api/store/types';
import { setReservationCheckoutData } from '../RTK/utility';
import { selectReservationCheckoutData } from '../RTK/utility/selectors';
import { ReservationCheckout } from '../Screens/ReservationCheckout/types';
import {
  CheckAvailabilityRef,
  ValidatePayload,
} from '../Screens/ReservationEventDetails/components/CheckAvailability';

import useAlert from './useAlert';

type ShowBookingSchedulePicker = {
  filter: {
    date?: string;
    size: number;
    time?: string;
  };
  itemInfo: ItemInfo;
  storeInfo: Partial<StoreDetails>;
};

type SelectBookingSchedule = {
  dateAndTime: Partial<ValidatePayload>;
  itemInfo: ItemInfo &
    (
      | { type: ReservationType.EVENT; eventSelectedTs?: EventTimeSlot }
      | { type: ReservationType.DINING; experienceSelectedTs?: string }
    );
  size: number;
  storeInfo:
    | (EventDetails['store'] & {
        location?: undefined;
        store_hours?: undefined;
      })
    | StoreDetails;
  onError?: () => void;
  onSuccess?: (
    rcd?:
      | ReservationCheckout
      | { noAddOnsSelectionNeeded?: boolean; hasAddOnsGroup?: boolean },
  ) => void; // if rcd is pass on onSuccess, it means continue to payment
};

const useReservationBooking = () => {
  const alert = useAlert();
  const dispatch = useDispatch();
  const availabilityRef = useRef<CheckAvailabilityRef>();
  const rcd = useSelector(selectReservationCheckoutData); // rcd = Reservation Checkout Data

  const _isCanContinue = useCallback(
    ({
      itemId,
      date,
      time,
      size,
    }: {
      itemId: string;
      date: string;
      time: string;
      size: number;
    }) => {
      if (lodashIsEmpty(rcd)) {
        return false;
      } else {
        const isThisEventCheckedOut = Boolean(rcd.url) && rcd.itemId === itemId;
        const isSameSize = rcd.size === size;
        const isSameDateAndTime =
          rcd.selectedDate === date && rcd.selectedTime === time;
        const isExpired = DateHelper.isOlderThanNow({
          amount: 5,
          time: dayjs(rcd.checkout_date).valueOf(),
          unit: 'minutes',
        });
        return (
          isThisEventCheckedOut && isSameSize && isSameDateAndTime && !isExpired
        );
      }
    },
    [rcd],
  );

  const _showBookingSchedulePicker = async (
    data: ShowBookingSchedulePicker,
  ) => {
    const modalPrompt = constants.isWeb
      ? ModalManager.show(modals.BOOK_RESERVATION, {
          payload: data,
        })
      : modals.show(modals.BOOK_RESERVATION, data);
    const booking: { success?: boolean; data?: ReservationCheckout } =
      await modalPrompt;
    if (booking?.success) {
      return booking;
    }
    return undefined;
  };

  const _selectBookingSchedule = useCallback(
    ({
      dateAndTime,
      itemInfo,
      size,
      storeInfo,
      onError,
      onSuccess,
    }: SelectBookingSchedule) => {
      const isReservation = itemInfo.type === ReservationType.DINING;
      const verifyPayload = {
        id: isReservation ? itemInfo.item.id : itemInfo.id,
        party_size: size,
        date: dateAndTime.date,
        time: dateAndTime.time,
        type: itemInfo.type,
      };
      const isContinue = _isCanContinue({
        itemId: verifyPayload.id,
        date: dateAndTime.date,
        time: dateAndTime.time,
        size,
      });
      if (!availabilityRef.current) {
        throw new Error(
          'availabilityRef must be pass on ref of CheckAvailability component',
        );
      } else if (isContinue) {
        onSuccess?.(rcd);
      } else {
        availabilityRef.current.show(verifyPayload, {
          onSuccess: async (availPayload, availableItemIds) => {
            const addOnsData = isReservation
              ? [
                  {
                    name: 'Experiences',
                    description:
                      'Find the option that best suits your experience:',
                    data: lodashFilter(itemInfo.experiences, (exp) =>
                      // filter the experiences that is in the selected time slot
                      exp.time_slots.includes(itemInfo.experienceSelectedTs),
                    ).reduce((arr, data) => [...arr, ...data.items], []), // flat the items to single array
                    is_required: true,
                    minimum_number: 1,
                    maximum_number: 1,
                  },
                ]
              : [
                  {
                    name: 'Ticket Types',
                    description:
                      'Find the option that best suits your experience:',
                    data: lodashFilter(itemInfo.tickets, (ticket) =>
                      // filter the ticket that is in the selected time slot
                      ticket.time_slots.includes(itemInfo.eventSelectedTs.id),
                    ).map((t) => ({ ...t.item })), // flat the item instead of {..., item: {}} it will be {...item}
                    is_required: true,
                    minimum_number: 1,
                    maximum_number: 1,
                  },
                ];
            const checkoutData: ReservationCheckout = {
              ...rcd,
              addOns: [
                {
                  ...addOnsData[0]?.data?.[0],
                  isMultiple: false,
                  multipleLimit: addOnsData[0].maximum_number,
                  parentName: addOnsData[0].name,
                },
              ],
              addOnsGroup: addOnsData,
              address: isReservation
                ? storeInfo.location
                : itemInfo.location_address,
              availableDays: isReservation
                ? [
                    ...new Set(
                      lodashFilter(storeInfo.store_hours, {
                        is_open: true,
                      }).map((e) => Number(e.day)),
                    ),
                  ]
                : itemInfo.available_days,
              cancellationPolicy: isReservation
                ? storeInfo.reservation_cancellation_policy
                : itemInfo.cancellation_policy,
              description: isReservation
                ? itemInfo.item.description
                : itemInfo.description,
              enabledAddOns: isReservation
                ? addOnsData[0]?.data?.map((addOn) => addOn.id) // for now since we don't pass the invalid experience id on verify, we assume all addOns is avaialble on table experiences
                : availableItemIds,
              endDate: isReservation ? undefined : itemInfo.end_date,
              formData: undefined,
              isReservation,
              itemId: availPayload.id,
              itemName: itemInfo.name,
              price: Number(addOnsData[0]?.data?.[0]?.price),
              selectedDate: dateAndTime.date,
              selectedTime: dateAndTime.time,
              size: availPayload.party_size,
              storeId: storeInfo.id,
              storeName: storeInfo.name,
              type: verifyPayload.type,
            };
            dispatch(setReservationCheckoutData(checkoutData));
            await new Promise((r) => setTimeout(r, 0)); // wait to propagate the dispatch before calling on success
            onSuccess?.({
              noAddOnsSelectionNeeded:
                checkoutData?.addOnsGroup?.[0]?.data?.length <= 1,
              hasAddOnsGroup: !lodashIsEmpty(checkoutData.addOnsGroup),
            });
          },
          onError: () => {
            const date = dayjs(dateAndTime.date).format('MMMM DD, YYYY');
            const time = dayjs(dateAndTime.time, 'HH:mm').format('hh:mm A');
            alert(
              'Ooops!',
              verifyPayload.party_size > 1
                ? "There aren't enough slots available for your selected group size. Try reducing the number of guests or choosing a different date."
                : `It looks like the selected ${date} and ${time} is already fully booked. Please try another available date or time.`,
              { web: { buttonStatus: 'info' } },
            );
            onError?.();
          },
        });
      }
    },
    [rcd, _isCanContinue, alert, dispatch],
  );

  const _navigateToCheckoutDestination = async (navigation, data) => {
    if (data?.hasAddOnsGroup && !data?.noAddOnsSelectionNeeded) {
      // if has add ons group selection and if it contains more than 1 selection
      navigation.navigate(routeList.RES_CHECKOUT_ADDONS);
    } else if (data?.url) {
      navigation.navigate(routeList.CHECKOUT_WEBVIEW, data);
    } else {
      await new Promise((r) => setTimeout(r, 0)); // wait to propagate the dispatch before navigating
      navigation.navigate(routeList.RES_CHECKOUT);
    }
  };

  return {
    availabilityRef,
    isCanContinue: _isCanContinue,
    showBookingSchedulePicker: _showBookingSchedulePicker,
    selectBookingSchedule: _selectBookingSchedule,
    navigateToCheckoutDestination: _navigateToCheckoutDestination,
  };
};

export default useReservationBooking;
